import { Order } from '@wix/ambassador-pricing-plans-v2-order/types';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { Plan, Period, PricingVariant } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { isPlanV3 } from '@wix/pricing-plans-utils';
import { IWixWindow } from '@wix/yoshi-flow-editor';
import pricingPlans from '../../.application.json';
import {
  ANALYTICS_CATEGORY_MONTHLY,
  ANALYTICS_CATEGORY_ONE_TIME,
  ANALYTICS_CATEGORY_WEEKLY,
  ANALYTICS_CATEGORY_YEARLY,
  getPlanCategory,
  getOrigin,
} from '../utils/analytics';
import { getPrice } from '../utils/plan';

const getVariantCategory = (variant: PricingVariant): string => {
  const billingTerms = variant.billingTerms;
  if (!billingTerms?.billingCycle?.period || billingTerms.endType !== 'UNTIL_CANCELLED') {
    return ANALYTICS_CATEGORY_ONE_TIME;
  }
  switch (billingTerms.billingCycle.period) {
    case Period.WEEK:
      return ANALYTICS_CATEGORY_WEEKLY;
    case Period.YEAR:
      return ANALYTICS_CATEGORY_YEARLY;
    default:
      return ANALYTICS_CATEGORY_MONTHLY;
  }
};

const getVariantPrice = (variant: PricingVariant): number =>
  parseFloat(variant.pricingStrategies?.[0]?.flatRate?.amount ?? '');

const { appDefinitionId } = pricingPlans;

export class Analytics {
  constructor(protected window: IWixWindow, protected referralInfo?: string) {}

  setReferralInfo(referralInfo?: string) {
    this.referralInfo = referralInfo;
  }

  addProductImpression(plans: (PublicPlan | Plan)[]) {
    this.window.trackEvent('AddProductImpression', {
      appDefId: appDefinitionId,
      origin: getOrigin(this.referralInfo),
      contents: plans.map((plan) => ({
        id: plan.id,
        name: plan.name,
        category: isPlanV3(plan) ? getVariantCategory(plan.pricingVariants![0]) : getPlanCategory(plan),
        price: isPlanV3(plan) ? getVariantPrice(plan.pricingVariants![0]) : getPrice(plan),
        currency: isPlanV3(plan) ? plan.currency : plan.pricing?.price?.currency,
        quantity: 1,
      })),
    });
  }

  clickProduct(plan: PublicPlan | Plan) {
    this.window.trackEvent('ClickProduct', {
      appDefId: appDefinitionId,
      origin: getOrigin(this.referralInfo),
      name: plan.name ?? undefined,
      category: isPlanV3(plan) ? getVariantCategory(plan.pricingVariants![0]) : getPlanCategory(plan),
      price: isPlanV3(plan) ? getVariantPrice(plan.pricingVariants![0]) : getPrice(plan),
      currency: isPlanV3(plan) ? plan.currency : plan.pricing?.price?.currency,
    });
  }

  purchase(plan: PublicPlan | Plan, order: Order, confirmed: 'yes' | 'no' | 'unknown' = 'unknown') {
    const price = isPlanV3(plan) ? getVariantPrice(plan.pricingVariants![0]) : getPrice(plan);
    const currency = isPlanV3(plan) ? plan.currency : plan.pricing?.price?.currency;
    this.window.trackEvent('Purchase', {
      appDefId: appDefinitionId,
      origin: getOrigin(this.referralInfo),
      id: order.id,
      // @ts-expect-error
      orderId: order.id,
      confirmed,
      revenue: price,
      currency,
      contents: [
        {
          id: plan.id,
          name: plan.name,
          category: isPlanV3(plan) ? getVariantCategory(plan.pricingVariants![0]) : getPlanCategory(plan),
          price,
          quantity: 1,
          productType: 'PRODUCT',
        },
      ],
    });
  }

  viewContent(plan: PublicPlan | Plan) {
    this.window.trackEvent('ViewContent', {
      appDefId: appDefinitionId,
      origin: getOrigin(this.referralInfo),
      name: plan.name ?? undefined,
      category: isPlanV3(plan) ? getVariantCategory(plan.pricingVariants![0]) : getPlanCategory(plan),
      price: isPlanV3(plan) ? getVariantPrice(plan.pricingVariants![0]) : getPrice(plan),
      currency: isPlanV3(plan) ? plan.currency : plan.pricing?.price?.currency,
    });
  }

  selectPayment(payment: string) {
    this.window.trackEvent('CustomEvent', {
      // @ts-expect-error
      event: 'SelectPayment',
      appDefId: appDefinitionId,
      origin: getOrigin(this.referralInfo),
      payment_method: payment,
    });
  }

  initiateCheckout(plan: PublicPlan | Plan) {
    this.window.trackEvent('InitiateCheckout', {
      appDefId: appDefinitionId,
      origin: getOrigin(this.referralInfo),
      contents: [
        {
          id: plan.id,
          name: plan.name,
          category: isPlanV3(plan) ? getVariantCategory(plan.pricingVariants![0]) : getPlanCategory(plan),
          price: isPlanV3(plan) ? getVariantPrice(plan.pricingVariants![0]) : getPrice(plan),
          currency: isPlanV3(plan) ? plan.currency : plan.pricing?.price?.currency,
          quantity: 1,
        },
      ],
    });
  }
}
